import { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import useConfirm from '../../../components/ConfirmDialog/useConfirm';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const useSignDetailFunctions = () => {
  const { id } = useParams();
  const history = useHistory();
  // Own the confirm modal here — return ConfirmPortal in the hook's
  // result so the parent component can render it as a sibling.
  const { confirm, ConfirmPortal } = useConfirm();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resealing, setResealing] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const fetchRequest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE_URL}/signatures/${id}`, { withCredentials: true });
      setRequest(res.data?.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleRemind = async () => {
    setReminding(true);
    setActionMsg('');
    try {
      const res = await axios.post(`${BASE_URL}/signatures/${id}/remind`, {}, { withCredentials: true });
      const reminded = res.data?.data?.reminded || [];
      setActionMsg(reminded.length ? `Reminder sent to: ${reminded.join(', ')}` : 'No signers eligible for reminder (rate limited to once per 24h)');
    } catch (e) {
      setActionMsg(e.response?.data?.message || 'Failed to send reminder');
    } finally {
      setReminding(false);
    }
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: 'Cancel signature request?',
      message: 'The request will be marked cancelled and signers will no longer be able to sign. This cannot be undone.',
      confirmText: 'Cancel Request',
      cancelText: 'Keep Open',
      variant: 'danger',
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await axios.post(`${BASE_URL}/signatures/${id}/cancel`, {}, { withCredentials: true });
      setRequest(prev => ({ ...prev, status: 'cancelled' }));
    } catch (e) {
      setActionMsg(e.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${BASE_URL}/signatures/${id}/download`, { withCredentials: true });
      window.open(res.data?.data?.url, '_blank');
    } catch (e) {
      setActionMsg(e.response?.data?.message || 'Failed to get download link');
    } finally {
      setDownloading(false);
    }
  };

  // Retry the sealed-PDF generation for a seal_failed request. Backend
  // gates this to eSign.reseal (ORG_ADMINS); the frontend button is also
  // gated via PermissionGate so non-admin users don't see it. On success
  // the request transitions seal_failed → completed and completion emails
  // are sent server-side.
  const handleReseal = async () => {
    const ok = await confirm({
      title: 'Retry sealing?',
      message: 'A successful reseal will send completion emails to every signer. Proceed?',
      confirmText: 'Retry Seal',
      variant: 'warning',
    });
    if (!ok) return;
    setResealing(true);
    setActionMsg('');
    try {
      await axios.post(`${BASE_URL}/signatures/${id}/reseal`, {}, { withCredentials: true });
      await fetchRequest();
      setActionMsg('Signature request resealed successfully — completion emails sent to signers.');
    } catch (e) {
      setActionMsg(e.response?.data?.message || 'Reseal failed. The request is still in seal_failed state.');
    } finally {
      setResealing(false);
    }
  };
  return {
    id,
    history,
    request,
    setRequest,
    loading,
    setLoading,
    error,
    setError,
    reminding,
    setReminding,
    cancelling,
    setCancelling,
    downloading,
    setDownloading,
    actionMsg,
    setActionMsg,
    fetchRequest,
    handleRemind,
    handleCancel,
    handleDownload,
    resealing,
    handleReseal,
    // Parent MUST render this or the confirm modals will never appear.
    ConfirmPortal,
  };
};

export default useSignDetailFunctions;
